/* eslint-disable react/prop-types */
import React, { useState, useCallback, useEffect } from "react";
import { useAuth } from "react-oidc-context";
import { Control, Controller } from "react-hook-form";
import {
  Box,
  Button,
  Stack,
  Tab,
  Tabs,
  Grid,
  Typography,
  TextField,
  InputAdornment,
} from "@mui/material";
import {
  ModalProps,
  createUseModal,
  useFormModal,
} from "@imminently/react/context/modals";
import {
  Dialog,
  DialogActions,
  DialogContent,
} from "@imminently/react/ui/core/dialog";
import { Text } from "@imminently/react/ui/core/text";
import {
  FormInputText,
  FormInputNumber,
  FormToggleGroup,
  FormInputSelect,
} from "@imminently/react/ui/forms";
import { yupResolver } from "@hookform/resolvers/yup";
import { PropsWithChildren } from "react";
import * as yup from "yup";

import { scrollable } from "@/components/scrollable";
import { CONSC_LABELS } from "@/resources/constants";
import {
  obsMetricNames,
  obsMetricToLabel,
  ObsMetricAlias,
} from "@/resources/observations_metricUtils";

import {
  checkFields,
  fieldChecks,
  AlertType,
} from "@/resources/observations_validations";
import { Observation, Consciousness, Rhythm, PatientEncounter, BloodPressureRange } from "@/resources/types";
import { encounterService, useLiveNextObservations } from "@/services";
import { css } from "@/theme";
import global from "@/global";
import { getLastRecordedObservationValues } from "@/util/util-obs";
import { GlasgowComaScore, GlasgowComaScoreProps } from "./GlasgowComaScore";
import { Pupils, PupilsProps } from "./Pupils";
import { LimbMovement, LimbMovementProps } from "./LimbMovement";
import { LimbNeurovascular, LimbNeurovascularProps } from "./LimbNeurovascular";
import { useConfigContext } from "@imminently/react/context/config";
import { ActingAsBanner } from "@/components/ActingAsBanner";
import { VoiceToTextButton } from "@/components/VoiceToTextButton";
import { useActAs } from "@/contexts/ActAsContext";
import { modalStyles } from "../styles";

const { queryClient, client } = global;

const stylesCSS = {
  danger: css`
      & .MuiOutlinedInput-notchedOutline  {
        border: 4px solid #E1B8FC !important;
      }
    `,
  warning: css`
      & .MuiOutlinedInput-notchedOutline {
        border-color: 4px solid #FF8680 !important;
      }
    `,
  purple_box: css`
      background-color: #E1B8FC;
      color: #420B66;
      border-radius: 8px;
      position: relative;
      bottom: -10px;
      & .MuiButtonBase-root {
        background-color: #420B66;
        color: white;
      }
    `,
  purple_input: css`
      & .MuiOutlinedInput-notchedOutline {
        border:2px solid #E1B8FC !important;
      }
    `,
  red_box: css`
      background-color: #FF8680;
      color: #6B201C;
      border-radius: 8px;
      position: relative;
      bottom: -10px;
      & .MuiButtonBase-root {
        background-color: #6B201C;
        color: white;
      }
      & p {
        padding: 0;
        font-size: 13px;
      }
    `,
  red_input: css`
      & .MuiOutlinedInput-notchedOutline {
        border:2px solid #FF8680 !important;
      }
    `,
  yellow_box: css`
    background-color: #f9f9cc;
    color: #66470A;
    border-radius: 8px;
    position: relative;
    bottom: -10px;
    & .MuiButtonBase-root {
      background-color: #66470A;
      color: white;
    }
  `,
  yellow_input: css`
    & .MuiOutlinedInput-notchedOutline {
      border:2px solid #f9f9cc !important;
    }
  `,

  orange_box: css`
    background-color: #f5e7ce;
    color: #66470A;
    border-radius: 8px;
    position: relative;
    bottom: -10px;
    & .MuiButtonBase-root {
      background-color: #66470A;
      color: white;
    }
    `,
  orange_input: css`
    & .MuiOutlinedInput-notchedOutline {
      border:2px solid #f5e7ce !important;
    }
    `,
  aknowledge_box: css`
      padding: 8px;
      font-size: 12px;
    `,

};

const allowEmpty = (schema: any) => {
  return yup.lazy((value: any) => (value === "" ? yup.string() : schema));
};

const validateSystolic = () => {
  return yup.lazy((value: any) => {
    if (value === "") return yup.string().required("Systolic required");

    return yup
      .number()
      .min(yup.ref("bloodPresDiastolic"), "Must be greater than Diastolic")
      .required("Systolic required");
  });
};

const validateDiastolic = () => {
  return yup.lazy((value: any) => {
    if (value === "") return yup.string().required("Diastolic required");

    return yup
      .number()
      .max(yup.ref("bloodPresSystolic"), "Must be less than Systolic")
      .required("Diastolic required");
  });
};

const schema = yup.object().shape({
  [obsMetricNames.respirations]: allowEmpty(yup.number().min(0)),
  [obsMetricNames.o2Sats]: allowEmpty(yup.number().min(0)),
  [obsMetricNames.o2Flow]: allowEmpty(yup.number().min(0)),
  [obsMetricNames.heartRate]: allowEmpty(yup.number().min(0)),
  [obsMetricNames.hypercapnicRespiratoryFailure]: allowEmpty(yup.boolean()),
  bloodPresSystolic: yup.string().when(["bloodPresDiastolic"], {
    is: (diastolic: any) => diastolic !== "",
    then: validateSystolic,
    otherwise: () => allowEmpty(yup.number().min(0)),
  }),
  bloodPresDiastolic: yup.string().when(["bloodPresSystolic"], {
    is: (systolic: any) => systolic !== "",
    then: validateDiastolic,
    otherwise: () => allowEmpty(yup.number().min(0)),
  }),
  [obsMetricNames.temperature]: allowEmpty(yup.number().min(0)),
  [obsMetricNames.consciousness]: allowEmpty(yup.mixed().oneOf(Object.values(Consciousness)).required()),
  [obsMetricNames.rhythm]: allowEmpty(yup.mixed().oneOf(Object.values(Rhythm)).required()),
  [obsMetricNames.bloodGlucose]: allowEmpty(yup.number().min(0)),
  [obsMetricNames.bloodAlcohol]: allowEmpty(yup.number().min(0)),
  [obsMetricNames.painScore]: allowEmpty(yup.number().min(0)),
  note: yup.string(),
  bedside: yup.object().shape({
    bloodPressure: yup.object().shape({
      leftArm: yup.object().shape({
        systolic: allowEmpty(yup.number().min(0)),
        diastolic: allowEmpty(yup.number().min(0)),
      }),
      rightArm: yup.object().shape({
        systolic: allowEmpty(yup.number().min(0)),
        diastolic: allowEmpty(yup.number().min(0)),
      }),
      laying: yup.object().shape({
        systolic: allowEmpty(yup.number().min(0)),
        diastolic: allowEmpty(yup.number().min(0)),
      }),
      standing: yup.object().shape({
        systolic: allowEmpty(yup.number().min(0)),
        diastolic: allowEmpty(yup.number().min(0)),
      }),
    }),
    heartRate: allowEmpty(yup.number().min(0)),
  }),
  ventilator: yup.object().shape({
    tidalVolume: allowEmpty(yup.number().min(0)),
    minuteVentilation: allowEmpty(yup.number().min(0)),
    frequencyRate: allowEmpty(yup.number().min(0)),
    peakPressure: allowEmpty(yup.number().min(0)),
    endTidalCarbonDioxide: allowEmpty(yup.number().min(0)),
  }),
}, [
  ["bloodPresSystolic", "bloodPresDiastolic"]
]);

const initialStateNeuro: Observation["neuro"] = {
  // eyeOpening: 4,
  // verbal: 5,
  // motor: 6,
  // pupil: {
  //   leftSize: 4,
  //   rightSize: 4,
  //   leftReaction: "+",
  //   rightReaction: "+",
  // },
  // movement: {
  //   armLeft: "normal",
  //   armRight: "normal",
  //   legLeft: "normal",
  //   legRight: "normal",
  // },
  // limbNeuro: {
  //   leftArm: {
  //     colour: "normal",
  //   },
  //   rightArm: {
  //     colour: "normal",
  //   },
  //   leftLeg: {
  //     colour: "normal",
  //   },
  //   rightLeg: {
  //     colour: "normal",
  //   },
  // },
};
const initialState = {
  [obsMetricNames.respirations]: "",
  [obsMetricNames.o2Sats]: "",
  [obsMetricNames.o2Flow]: "",
  [obsMetricNames.heartRate]: "",
  [obsMetricNames.hypercapnicRespiratoryFailure]: false,
  bloodPresSystolic: "",
  bloodPresDiastolic: "",
  [obsMetricNames.temperature]: "",
  [obsMetricNames.consciousness]: Consciousness.NA,
  [obsMetricNames.rhythm]: "",
  [obsMetricNames.painScore]: "",
  [obsMetricNames.bloodGlucose]: "",
  [obsMetricNames.bloodAlcohol]: "",
  bedside: {
    bloodPressure: {
      leftArm: {
        systolic: "",
        diastolic: "",
      },
      rightArm: {
        systolic: "",
        diastolic: "",
      },
      laying: {
        systolic: "",
        diastolic: "",
      },
      standing: {
        systolic: "",
        diastolic: "",
      },
    },
    heartRate: "",
  },
  ventilator: {
    tidalVolume: "",
    minuteVentilation: "",
    frequencyRate: "",
    peakPressure: "",
    endTidalCarbonDioxide: "",
  },
  note: "",
  neuro: initialStateNeuro,
};

const rhythmOptions = Object.values(Rhythm).map((value) => ({ value, label: value }));

type InputWrapperProps = { label: string; units?: string } & PropsWithChildren;

/**
 * Provides label, units and the ability to put multiple inputs on a single line
 */
const InputWrapper = (props: InputWrapperProps) => {
  const { label, children, units } = props;

  return (
    <Stack gap={1}>
      <Text bold>{label}</Text>
      <Stack direction="row" gap={2} alignItems="center">
        <Stack direction="row" flex={units ? 10 : 12} gap={2}>
          {children}
        </Stack>
        {/* padding needs to match input fields error area */}
        {units && (<Text flex={2} pb={2.25} bold>{units}</Text>)}
      </Stack>
    </Stack>
  );
};

const parseObject = (value: any) => JSON.stringify(value) === "{}" || value === undefined ? undefined : value;

const parseNum = (value: string | undefined) =>
  value === "" || value === undefined ? undefined : Number(value);

const consciousnessOptions = Object.values(Consciousness).map((c) => ({
  value: c,
  label: CONSC_LABELS[c],
}));

const painOptions = Array.from({ length: 11 }, (_, i) => i).map((n) => ({
  value: n.toString(),
  label: `${n}`,
}));

type AddModalProps = {
  encounter: PatientEncounter;
  observation?: Observation; // Optional - if provided, we're in edit mode
  mode?: "add" | "edit" | "create"; // Explicitly specify the mode
  customSubmitHandler?: (observationData: any) => Promise<PatientEncounter>; // Custom handler for initial observation flow
  onObservationSaved?: () => void | Promise<void>;
};

// TAB Panel
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

export function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          <Typography component={"span"} variant={"body2"}>{children}</Typography>
        </Box>
      )}
    </div>
  );
}

const CustomFormInputNumber: React.FC<{
  control: Control<any>;
  name: string;
  placeholder?: string;
  [key: string]: any;
}> = ({ control, name, placeholder, ...props }) => {
  return (
    <Controller
      name={name}
      control={control}
      // eslint-disable-next-line @typescript-eslint/no-shadow
      render={({ field: { name, value } }) => {
        return (
          <FormInputNumber
            control={control}
            name={name}
            fullWidth
            placeholder={placeholder}
            {...props}
            css={value && (stylesCSS as any)[fieldChecks[name as keyof typeof fieldChecks]?.(value)]}
          />
        );
      }}
    />
  );
};

const BooleanToggleGroup: React.FC<{
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  control: Control<any>;
  name: string;
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  [key: string]: any;
}> = ({ control, name, ...props }) => {
  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { onChange, value } }) => (
        <FormToggleGroup
          {...props}
          exclusive={true} // i.e. behave like a radio button group
          control={control}
          name={name}
          value={value?.toString()}
          onChange={(e) => onChange((e.target as HTMLInputElement).value === "true")}
          options={[
            { value: "true", label: "Yes" },
            { value: "false", label: "No" }
          ]}
        />
      )}
    />
  );
};

type AlertField = {
  field: ObsMetricAlias,
  type: AlertType
}

/* eslint-disable complexity */
const AddObservationModal = ({ props }: ModalProps<AddModalProps>) => {
  const auth = useAuth();
  const { actAsState } = useActAs();
  const signedInName = auth.user?.profile?.name ?? "?";
  const name = actAsState.isActingAs ? `${actAsState.displayName ?? "?"} (signed in as ${signedInName})` : signedInName;
  const [alertFields, setAlertFields] = useState<AlertField[]>([]);
  const [acknowledgedFields, setAcknowledgedFields] = useState<string[]>([]);
  const [isCustomLoading, setIsCustomLoading] = useState(false);
  const { refetch } = useLiveNextObservations();
  const config = useConfigContext();

  const isEditMode = props.mode === "edit" && props.observation;
  // customSubmitHandler / isInitialObservationFlow are no longer used:
  // the encounter is now created eagerly before this modal opens, so the
  // standard subresource hooks always receive a real encounter ID.
  // const customSubmitHandler = props.customSubmitHandler;
  // const isInitialObservationFlow = customSubmitHandler && props.encounter.id === "pending";

  const subresourceHooks = encounterService.useSubresource(
    "observations",
    props.encounter.id
  );

  const { mutateAsync: createObservation, isLoading: isCreating } = subresourceHooks.useCreate({
    onSuccess: () => {
      queryClient.refetchQueries(["encounters", props.encounter.id]);
      refetch();
    },
  });

  const { mutateAsync: updateObservation, isLoading: isUpdating } = subresourceHooks.useUpdate({
    onSuccess: () => {
      queryClient.refetchQueries(["encounters", props.encounter.id]);
      refetch();
    },
  });

  const isLoading = isCreating || isUpdating || isCustomLoading;

  // Prepare default values based on mode
  const getDefaultValues = () => {
    if (isEditMode && props.observation) {
      const obs = props.observation;
      return {
        [obsMetricNames.respirations]: obs.respirations?.toString() || "",
        [obsMetricNames.o2Sats]: obs.o2Sats?.toString() || "",
        [obsMetricNames.o2Flow]: obs.o2Flow?.toString() || "",
        [obsMetricNames.heartRate]: obs.heartRate?.toString() || "",
        [obsMetricNames.hypercapnicRespiratoryFailure]: obs.hypercapnicRespiratoryFailure || false,
        bloodPresSystolic: obs.bloodPressure?.systolic?.toString() || "",
        bloodPresDiastolic: obs.bloodPressure?.diastolic?.toString() || "",
        [obsMetricNames.temperature]: obs.temperature?.toString() || "",
        [obsMetricNames.consciousness]: obs.consciousness || Consciousness.NA,
        [obsMetricNames.rhythm]: obs.rhythm || "",
        [obsMetricNames.painScore]: obs.painScore?.toString() || "",
        [obsMetricNames.bloodGlucose]: obs.bloodGlucose?.toString() || "",
        [obsMetricNames.bloodAlcohol]: obs.bloodAlcohol?.toString() || "",
        bedside: obs.bedside || initialState.bedside,
        ventilator: obs.ventilator || initialState.ventilator,
        note: obs.note || "",
        neuro: obs.neuro || initialStateNeuro,
      };
    }
    return {
      ...initialState,
      [obsMetricNames.hypercapnicRespiratoryFailure]: getLastRecordedObservationValues(props.encounter.observations).hypercapnicRespiratoryFailure,
    };
  };

  const { methods, notify, close } = useFormModal({
    mode: "all",
    // Cast to any: yupResolver currently has an incompatible inferred type and TS don't like
    resolver: yupResolver(schema) as any,
    defaultValues: getDefaultValues(),
  });
  const { control, formState: { isValid }, handleSubmit, trigger, getValues, setValue, watch } = methods;

  const handleAdd = async (data: any) => {
    const { note, bloodPresSystolic, bloodPresDiastolic, bedside, ventilator } = data;

    let mainBloodPressure = undefined;
    if (bloodPresSystolic !== "" && bloodPresDiastolic !== "") {
      mainBloodPressure = {
        diastolic: parseNum(bloodPresDiastolic),
        systolic: parseNum(bloodPresSystolic),
      } as BloodPressureRange;
    }

    const consciousness =
      data.consciousness === Consciousness.NA ? undefined : data.consciousness;

    const newObservation: Partial<Observation> = {
      respirations: parseNum(data.respirations),
      o2Sats: parseNum(data.o2Sats),
      o2Flow: parseNum(data.o2Flow),
      heartRate: parseNum(data.heartRate),
      hypercapnicRespiratoryFailure: data.hypercapnicRespiratoryFailure ?? false,
      bloodPressure: mainBloodPressure,
      temperature: parseNum(data.temperature),
      consciousness,
      rhythm: data.rhythm === "" ? undefined : data.rhythm,
      painScore: parseNum(data.painScore),
      bloodGlucose: parseNum(data.bloodGlucose),
      bloodAlcohol: parseNum(data.bloodAlcohol),
      note: (note === "" || note === null) ? undefined : note,
      bedside: parseObject({
        bloodPressure: parseObject({
          leftArm: parseObject({
            systolic: parseNum(bedside.bloodPressure.leftArm.systolic),
            diastolic: parseNum(bedside.bloodPressure.leftArm.diastolic)
          }),
          rightArm: parseObject({
            systolic: parseNum(bedside.bloodPressure.rightArm.systolic),
            diastolic: parseNum(bedside.bloodPressure.rightArm.diastolic)
          }),
          laying: parseObject({
            systolic: parseNum(bedside.bloodPressure.laying.systolic),
            diastolic: parseNum(bedside.bloodPressure.laying.diastolic)
          }),
          standing: parseObject({
            systolic: parseNum(bedside.bloodPressure.standing.systolic),
            diastolic: parseNum(bedside.bloodPressure.standing.diastolic)
          })
        }),
        heartRate: parseNum(bedside.heartRate)
      }),
      ventilator: parseObject({
        tidalVolume: parseNum(ventilator.tidalVolume),
        minuteVentilation: parseNum(ventilator.minuteVentilation),
        frequencyRate: parseNum(ventilator.frequencyRate),
        peakPressure: parseNum(ventilator.peakPressure),
        endTidalCarbonDioxide: parseNum(ventilator.endTidalCarbonDioxide)
      }),
      neuro: data.neuro,
    };

    try {
      // Legacy custom submit handler flow (no longer used - encounter is now
      // created eagerly before this modal opens, so we always have a real ID):
      //
      // if (isInitialObservationFlow && customSubmitHandler) {
      //   setIsCustomLoading(true);
      //   const createdEncounter = await customSubmitHandler(newObservation);
      //   await client.post(`encounters/${createdEncounter.id}/observations`, newObservation);
      //   queryClient.refetchQueries(["encounters", createdEncounter.id]);
      //   refetch();
      //   close(createdEncounter as any);
      // } else
      if (isEditMode && props.observation) {
        await updateObservation({ id: props.observation.id, payload: newObservation });
        await props.onObservationSaved?.();
        close(true);
      } else {
        // Standard flow: encounter already exists
        await createObservation(newObservation);
        await props.onObservationSaved?.();
        close(true);
      }
    } catch (e: any) {
      notify.error(e.message ?? `Failed to ${isEditMode ? "update" : "add"} observation.`);
      return;
    } finally {
      setIsCustomLoading(false);
    }
  };


  const acknowledgeAlert = (field: string) => {

    const newAlertFields = alertFields.map((a) => {
      if (a.field !== field) {
        return a; // We still need to acknowledge this one
      } else {
        const noteValue = getValues("note");
        const acknowledgeStatement = `${name} acknowledged ${obsMetricToLabel(a.field)} risk`;

        setValue("note", `${noteValue} ${acknowledgeStatement}\n`);
      }
    }).filter(x => Boolean(x)) as AlertField[];

    setAlertFields(newAlertFields);
    setAcknowledgedFields(prev => [...prev, field]);
  };



  //function to check on key press up
  const checkForm = useCallback(() => {
    const values = getValues(); // Get current form values
    const newAlertFields: AlertField[] = [];

    const hasHypercapnicRespiratoryFailure = values.hypercapnicRespiratoryFailure;

    // Get current O2 flow, converting from string if needed
    let currentO2Flow: number | null = null;
    if (values.o2Flow !== undefined && values.o2Flow !== "") {
      const converted = Number(values.o2Flow);
      if (!Number.isNaN(converted)) {
        currentO2Flow = converted;
      }
    } else {
      // If not entered in current form, check if the last recorded o2Flow was above zero
      // If it was above zero, we assume patient is still on oxygen; if not, assume they're off oxygen (0)
      const lastRecordedO2FlowAboveZero = getLastRecordedObservationValues(props.encounter.observations).o2FlowAboveZero;
      currentO2Flow = lastRecordedO2FlowAboveZero ? 1 : 0; // Use 1 as a safe non-zero value, 0 for zero flow
    }

    for (const [field, value] of Object.entries(values)) {
      // Skip if the field is already acknowledged
      if (acknowledgedFields.includes(field as ObsMetricAlias)) {
        continue;
      }
      // biome-ignore lint/suspicious/noExplicitAny: <explanation>
      const alertType = checkFields(field as ObsMetricAlias, value as any, {
        hasHypercapnicRespiratoryFailure,
        currentO2Flow
      });
      if (alertType !== AlertType.DEFAULT) {
        newAlertFields.push({ field: field as ObsMetricAlias, type: alertType });
      }
    }
    setAlertFields(newAlertFields);
  }, [getValues, acknowledgedFields, props.encounter.observations]);

  // Call checkForm on initial mount to ensure default values (like hypercapnicRespiratoryFailure) are used in validation
  useEffect(() => {
    checkForm();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [selectedTab, setSelectedTab] = useState(0);
  let tabItems = ["Vital signs"];
  if (config.app.enableNeuro !== "false") {
    tabItems = tabItems.concat([
      "Glasgow Coma Scale",
      "Pupils",
      "Limb Movement",
      "Limb Neurovascular",
    ]);
  }
  tabItems = tabItems.concat([
    "Post/Bilat BP",
    "Ventilator"
  ]);
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
  };

  const AlertField = ({alert}: {alert: AlertField}) => {
    let AlertMessage: React.ReactNode;
    switch (alert.type) {
      case AlertType.DANGER:
        AlertMessage = (
          <div>
            <b>Medical emergency team (MET) call</b>
            <p>Actions required</p>
            <ul>
              <li>Place emergency call</li>
              <li>Initiate ALS/BLS protocols</li>
              <li>If transfer is required, patient must be escorted by ALS staff member</li>
            </ul>
          </div>
        );
        break;
      case AlertType.WARNING:
        AlertMessage = (
          <div>
            <b>Medical Review (Registrar/Consultant)</b>
            <p>Actions required</p>
            <ul>
              <li>Record observations every 15 minutes</li>
              <li>Doctor to review patient within 30 minutes. If medical review not attended within 30 minutes, consider MET call</li>
              <li>iSoBAR - Medical Review sticker</li>
              <li>If transfer is required, patient must be escorted</li>
            </ul>
          </div>
        );
        break;
      case AlertType.ORANGE:
        AlertMessage = (
          <div>
            <b>Senior Nurse Review</b>
            <p>Actions required</p>
            <ul>
              <li>Record observations at least once every 1 hour</li>
              <li>Senior Nurse must review patient</li>
              <li>Senior Nurse to consider Medical review</li>
              <li>Senior Nurse review sticker</li>
              <li>Manage fever, pain, fluids, blood loss or distress and O2 Requirement</li>
              <li>If transfer is required, patient must be escorted</li>
            </ul>
          </div>
        );
        break;
      case AlertType.YELLOW:
        AlertMessage = (
          <div>
            <b>Increased surveillance</b>
            <p>Actions required</p>
            <ul>
              <li>Record observations at a minimum 4 hours</li>
              <li>Inform Shift Coordinator</li>
              <li>Carry out appropriate interventions as prescribed</li>
              <li>Manage fever, pain, fluids, blood loss or distress, O2 requirements</li>
            </ul>
          </div>
        );
        break;
      default:
      // No text is provided
    }
    return (
      <Box
        css={
          alert.type === AlertType.DANGER
            ? stylesCSS.purple_box
            : alert.type === AlertType.WARNING
              ? stylesCSS.red_box
              : alert.type === AlertType.YELLOW
                ? stylesCSS.yellow_box
                : alert.type === AlertType.ORANGE
                  ? stylesCSS.orange_box
                  : null
        }
      >
        <Box css={stylesCSS.aknowledge_box}>
          <div>
            <b>{obsMetricToLabel(alert.field)} requires acknowledgement</b>
          </div>
          {AlertMessage}
        </Box>
        <Button onClick={() => acknowledgeAlert(alert.field)}>
          I acknowledge this
        </Button>
      </Box>
    );
  };
  return (
    <>
      <Dialog open onClose={() => close()} onKeyUp={() => checkForm()} maxWidth="lg">
        <ActingAsBanner />
        <DialogContent sx={{ width: "67rem", p: 0, overflow: "overlay" }}>
          <Grid container spacing={0}>
            <Grid item xs={12} sm={4} md={4} sx={{ bgcolor: "#C3C8DE" }}>
              <Box css={modalStyles.container_left}>
                <Text variant="h3">{isEditMode ? "Edit" : "Add"} observations</Text>
                <Tabs
                  value={selectedTab}
                  onChange={handleTabChange}
                  orientation="vertical"
                  variant="scrollable"
                  css={modalStyles.tabs}
                >
                  {tabItems.map((item, index) => (
                    <Tab
                      css={[
                        modalStyles.tab,
                        (selectedTab === index) ? modalStyles.active_tab : null
                      ]}
                      key={item}
                      label={
                        <>
                          <Text
                            css={[
                              modalStyles.circle,
                              (selectedTab === index) ? modalStyles.active_circle : null
                            ]}
                          >
                            {index + 1}
                          </Text>
                          <Text variant="h5" css={modalStyles.tab_title}>
                            {item}
                          </Text>
                        </>
                      }
                      {...(selectedTab === index && { selected: true })}
                    />
                  ))}
                </Tabs>
                <Box css={modalStyles.notes_block}>
                  {(alertFields?.length > 0) && (
                    <AlertField alert={alertFields[0]} />
                  )}
                  <Box>
                    <Controller
                      control={control}
                      name="note"
                      render={({ field, fieldState }) => (
                        <TextField
                          {...field}
                          onChange={(e) => field.onChange(e.target.value)}
                          fullWidth
                          size="medium"
                          label="Observation notes..."
                          multiline
                          rows={7}
                          error={Boolean(fieldState.error)}
                          helperText={fieldState.error?.message}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end" sx={{ alignSelf: "flex-start", mt: 1.5, mr: 0.5 }}>
                                <VoiceToTextButton />
                              </InputAdornment>
                            ),
                          }}
                        />
                      )}
                    />
                  </Box>
                </Box>
              </Box>
            </Grid>

            <Grid item xs={12} sm={8} md={8} >
              <Box sx={{ height: "700px", maxHeight: "700px", overflow: "auto" }} className={scrollable}>
                <TabPanel value={selectedTab} index={tabItems.indexOf("Vital signs")}>
                  <Stack gap={0.5}>

                    <InputWrapper label={obsMetricToLabel("hypercapnicRespiratoryFailure")}>
                      <BooleanToggleGroup
                        fullWidth={false}
                        control={control}
                        name="hypercapnicRespiratoryFailure"
                        onClick={() => checkForm()}
                        sx={{
                          // just otherwise it looks odd, because all the other inputs have space for the helper text
                          mb: 2,
                        }}
                      />
                    </InputWrapper>

                    <InputWrapper label={obsMetricToLabel("respirations")} units="bpm" >
                      <CustomFormInputNumber fullWidth autoFocus control={control} name="respirations" placeholder="Breaths per min" />
                    </InputWrapper>

                    <Stack direction="row" width="100%" spacing={0}>
                      <Box flex={1}>
                        <InputWrapper label={obsMetricToLabel("o2Flow")} units="l/min">
                          <CustomFormInputNumber
                            fullWidth
                            control={control}
                            name="o2Flow"
                            placeholder="Litres per min"
                          />
                        </InputWrapper>
                      </Box>
                      <Box flex={1}>
                        <InputWrapper label={obsMetricToLabel("o2Sats")} units="%">
                          <CustomFormInputNumber
                            fullWidth
                            control={control}
                            name="o2Sats"
                            placeholder="Percentage"
                          />
                        </InputWrapper>
                      </Box>
                    </Stack>

                    <InputWrapper label={obsMetricToLabel("heartRate")} units="bpm">
                      <CustomFormInputNumber fullWidth control={control} name="heartRate" placeholder="Beats per min" />
                    </InputWrapper>

                    <InputWrapper label={obsMetricToLabel("bloodPressure")} units="mmHg">
                      <CustomFormInputNumber
                        fullWidth
                        control={control}
                        name="bloodPresSystolic"
                        placeholder="Systolic reading"
                        onBlur={() => trigger("bloodPresDiastolic")}
                      />
                      <CustomFormInputNumber
                        fullWidth
                        control={control}
                        name="bloodPresDiastolic"
                        placeholder="Diastolic reading"
                        onBlur={() => trigger("bloodPresSystolic")}
                      />
                    </InputWrapper>

                    <InputWrapper label={obsMetricToLabel("temperature")} units="°C">
                      <CustomFormInputNumber fullWidth control={control} name="temperature" placeholder="Celsius" />
                    </InputWrapper>

                    <InputWrapper label={obsMetricToLabel("consciousness")}>
                      <FormToggleGroup onClick={() => checkForm()} fullWidth control={control} name="consciousness" exclusive options={consciousnessOptions} />
                    </InputWrapper>

                    <InputWrapper label={obsMetricToLabel("rhythm")} units="">
                      <FormInputSelect fullWidth control={control} name="rhythm" options={rhythmOptions} label="Select rhythm" />
                    </InputWrapper>

                    <InputWrapper label={obsMetricToLabel("bloodGlucose")} units="mmol/L">
                      <CustomFormInputNumber fullWidth control={control} name="bloodGlucose" placeholder="Millimoles per litre" />
                    </InputWrapper>

                    <InputWrapper label={obsMetricToLabel("bloodAlcohol")} units="gram%">
                      <CustomFormInputNumber fullWidth control={control} name="bloodAlcohol" placeholder="Gram percentage" />
                    </InputWrapper>

                    <InputWrapper label={obsMetricToLabel("painScore")}>
                      <FormToggleGroup onClick={() => checkForm()} fullWidth control={control} name="painScore" exclusive options={painOptions} />
                    </InputWrapper>

                  </Stack>

                </TabPanel>
                {config.app.enableNeuro !== "false" ? (
                  <>
                    <TabPanel value={selectedTab} index={tabItems.indexOf("Glasgow Coma Scale")}>
                      <GlasgowComaScore control={control as unknown as GlasgowComaScoreProps[ "control" ]} />
                    </TabPanel>
                    <TabPanel value={selectedTab} index={tabItems.indexOf("Pupils")}>
                      <Pupils control={control as unknown as PupilsProps[ "control" ]} />
                    </TabPanel>
                    <TabPanel value={selectedTab} index={tabItems.indexOf("Limb Movement")}>
                      <LimbMovement
                        watch={watch as unknown as LimbMovementProps["watch"]}
                        setValue={setValue as unknown as LimbMovementProps["setValue"]}
                      />
                    </TabPanel>
                    <TabPanel value={selectedTab} index={tabItems.indexOf("Limb Neurovascular")}>
                      <LimbNeurovascular
                        watch={watch as unknown as LimbNeurovascularProps["watch"]}
                        setValue={setValue as LimbNeurovascularProps["setValue"]}
                      />
                    </TabPanel>
                  </>
                ) : null }
                <TabPanel value={selectedTab} index={tabItems.indexOf("Post/Bilat BP")}>
                  <Stack>
                    <InputWrapper label={obsMetricToLabel("bedsideBloodPressureLeftArm")} units="mmHg">
                      <CustomFormInputNumber
                        fullWidth
                        control={control}
                        name="bedside.bloodPressure.leftArm.systolic"
                        placeholder="Systolic reading"
                      />
                      <CustomFormInputNumber
                        fullWidth
                        control={control}
                        name="bedside.bloodPressure.leftArm.diastolic"
                        placeholder="Diastolic reading"
                      />
                    </InputWrapper>

                    <InputWrapper label={obsMetricToLabel("bedsideBloodPressureRightArm")} units="mmHg">
                      <CustomFormInputNumber
                        fullWidth
                        control={control}
                        name="bedside.bloodPressure.rightArm.systolic"
                        placeholder="Systolic reading"
                      />
                      <CustomFormInputNumber
                        fullWidth
                        control={control}
                        name="bedside.bloodPressure.rightArm.diastolic"
                        placeholder="Diastolic reading"
                      />
                    </InputWrapper>

                    <InputWrapper label={obsMetricToLabel("bedsideBloodPressureLaying")} units="mmHg">
                      <CustomFormInputNumber
                        fullWidth
                        control={control}
                        name="bedside.bloodPressure.laying.systolic"
                        placeholder="Systolic reading"
                      />
                      <CustomFormInputNumber
                        fullWidth
                        control={control}
                        name="bedside.bloodPressure.laying.diastolic"
                        placeholder="Diastolic reading"
                      />
                    </InputWrapper>

                    <InputWrapper label={obsMetricToLabel("bedsideBloodPressureStanding")} units="mmHg">
                      <CustomFormInputNumber
                        fullWidth
                        control={control}
                        name="bedside.bloodPressure.standing.systolic"
                        placeholder="Systolic reading"
                      />
                      <CustomFormInputNumber
                        fullWidth
                        control={control}
                        name="bedside.bloodPressure.standing.diastolic"
                        placeholder="Diastolic reading"
                      />
                    </InputWrapper>

                    <InputWrapper label={obsMetricToLabel("bedsideHeartRate")} units="bpm">
                      <CustomFormInputNumber fullWidth control={control} name="bedside.heartRate" placeholder="Beats per min" />
                    </InputWrapper>
                  </Stack>
                </TabPanel>
                <TabPanel value={selectedTab} index={tabItems.indexOf("Ventilator")}>
                  <Stack>
                    <InputWrapper label={obsMetricToLabel("ventilatorTidalVolume")} units="ml">
                      <CustomFormInputNumber fullWidth control={control} name="ventilator.tidalVolume" placeholder="Millilitres" />
                    </InputWrapper>
                    <InputWrapper label={obsMetricToLabel("ventilatorMinuteVentilation")} units="l/min">
                      <CustomFormInputNumber fullWidth control={control} name="ventilator.minuteVentilation" placeholder="Litres per min" />
                    </InputWrapper>
                    <InputWrapper label={obsMetricToLabel("ventilatorFrequencyRate")} units="bpm">
                      <CustomFormInputNumber fullWidth control={control} name="ventilator.frequencyRate" placeholder="Beats per min" />
                    </InputWrapper>
                    <InputWrapper label={obsMetricToLabel("ventilatorPeakPressure")} units="cmH2O">
                      <CustomFormInputNumber fullWidth control={control} name="ventilator.peakPressure" placeholder="Centimetres of water" />
                    </InputWrapper>
                    <InputWrapper label={obsMetricToLabel("ventilatorEndTidalCarbonDioxide")} units="mmHg">
                      <CustomFormInputNumber fullWidth control={control} name="ventilator.endTidalCarbonDioxide" placeholder="Millimetres of mercury" />
                    </InputWrapper>
                  </Stack>
                </TabPanel>
              </Box>
              <DialogActions>
                <Grid container>
                  <Grid item xs={6}>
                    {selectedTab > 0 && (
                      <Button variant="outlined" onClick={() => setSelectedTab(selectedTab - 1)}>
                        Previous
                      </Button>
                    )}
                    {selectedTab < tabItems.length - 1 && (
                      <Button variant="outlined" disabled={!isValid || isLoading} onClick={() => setSelectedTab(selectedTab + 1)}>
                        Next - {tabItems[selectedTab + 1]}
                      </Button>
                    )}
                  </Grid>
                  <Grid item xs={6} style={{ textAlign: "right" }}>
                    <Button
                      disabled={!isValid || alertFields.length != 0 || isLoading}
                      onClick={handleSubmit(handleAdd)}
                    >
                      Submit {selectedTab === 1 ? "GCS" : tabItems[selectedTab]}
                    </Button>
                  </Grid>
                </Grid>
              </DialogActions>
            </Grid>
          </Grid>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const useAddObservationModal = createUseModal(AddObservationModal);
export const useEditObservationModal = createUseModal(AddObservationModal);
